import { useEffect, useState } from "react";
import { Calendar, Search, X } from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { api, apiError } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";
import ExportCsvButton from "../../components/ExportCsvButton";

const STATUSES = ["", "pending_payment", "confirmed", "cancelled", "completed", "no_show"];

const TONE = {
  confirmed: "bg-teal-500/10 text-teal-300 border-teal-500/20",
  pending_payment: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  cancelled: "bg-zinc-800 text-mb-fg2",
  completed: "bg-zinc-800 text-mb-fg2",
  no_show: "bg-red-500/10 text-red-300 border-red-500/20",
};

export default function HospitalAppointments() {
  const [doctors, setDoctors] = useState([]);
  const [filters, setFilters] = useState({ doctor_id: "all", date_from: "", date_to: "", status: "all" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/hospital/doctors").then((r) => setDoctors(r.data));
  }, []);

  const refresh = () => {
    setLoading(true);
    const params = {};
    if (filters.doctor_id && filters.doctor_id !== "all") params.doctor_id = filters.doctor_id;
    if (filters.status && filters.status !== "all") params.status = filters.status;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    api.get("/hospital/appointments", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [filters]);

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/hospital/appointments/${cancelTarget.id}/cancel`, { reason: cancelReason });
      toast.success("Appointment cancelled & patient notified");
      setCancelTarget(null); setCancelReason("");
      refresh();
    } catch (e) {
      toast.error(apiError(e, "Cancel failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="hospital-appointments-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ appointments</div>
          <h1 className="mt-2 text-3xl font-light tracking-tight">Appointments</h1>
          <p className="text-sm text-mb-fg2 mt-1">Filter, view and cancel bookings across your doctors.</p>
        </div>
        <ExportCsvButton
          endpoint="/api/hospital/exports/appointments.csv"
          filename="appointments"
          params={{
            doctor_id: filters.doctor_id !== "all" ? filters.doctor_id : undefined,
            status: filters.status !== "all" ? filters.status : undefined,
            date_from: filters.date_from || undefined,
            date_to: filters.date_to || undefined,
          }}
          testid="hosp-appts-export"
        />
      </div>

      {/* filters */}
      <div className="rounded-xl border border-mb-border bg-mb-surface p-5 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">Doctor</Label>
          <Select value={filters.doctor_id} onValueChange={(v) => setFilters({ ...filters, doctor_id: v })}>
            <SelectTrigger data-testid="appt-filter-doctor" className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg"><SelectValue placeholder="All doctors" /></SelectTrigger>
            <SelectContent className="bg-mb-surface2 border-mb-border text-white">
              <SelectItem value="all">All doctors</SelectItem>
              {doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="appt-filter-status" className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent className="bg-mb-surface2 border-mb-border text-white">
              <SelectItem value="all">Any</SelectItem>
              {STATUSES.filter(Boolean).map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">From</Label>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            data-testid="appt-filter-from"
            className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
        </div>
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">To</Label>
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            data-testid="appt-filter-to"
            className="bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
        </div>
      </div>

      {/* table */}
      <div className="rounded-xl border border-mb-border bg-mb-surface overflow-hidden">
        <div className="hidden md:grid grid-cols-[1fr_1fr_1.2fr_0.8fr_0.8fr_auto] gap-4 px-5 py-3 border-b border-mb-border text-[10px] uppercase tracking-[0.25em] text-mb-fg2">
          <div>Patient</div><div>Doctor</div><div>Slot</div><div>Fee</div><div>Status</div><div className="text-right">Action</div>
        </div>
        {loading ? (
          <div className="px-6 py-10 text-sm text-mb-fg2 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-6 py-10 text-sm text-mb-fg2 text-center" data-testid="appts-empty">No appointments.</div>
        ) : (
          <ul className="divide-y divide-mb-border">
            {items.map((a) => (
              <li key={a.id} data-testid={`hospital-appt-row-${a.id}`}
                className="px-5 py-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_1.2fr_0.8fr_0.8fr_auto] gap-2 md:gap-4 md:items-center">
                <div className="text-sm text-white truncate">{a.patient?.full_name || "—"}<div className="text-xs text-mb-fg2 truncate">{a.patient?.email}</div></div>
                <div className="text-sm text-mb-fg2 truncate">{a.doctor?.full_name}<div className="text-xs text-mb-fg2 truncate">{a.doctor?.specialty}</div></div>
                <div className="text-sm text-mb-fg2 inline-flex items-center gap-2"><Calendar className="h-3 w-3 text-mb-fg2" />{new Date(a.slot_start).toLocaleString()}</div>
                <div className="text-sm text-mb-fg2">{formatCurrency(a.consultation_fee)}</div>
                <div><span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded border ${TONE[a.status] || "bg-zinc-800 text-mb-fg2"}`}>{a.status.replace("_", " ")}</span></div>
                <div className="flex justify-start md:justify-end">
                  <Button variant="ghost" disabled={["cancelled", "completed", "no_show"].includes(a.status)}
                    onClick={() => setCancelTarget(a)} data-testid={`hospital-cancel-${a.id}`}
                    className="hover:bg-red-500/10 text-red-400 h-9 disabled:opacity-30">
                    <X className="h-4 w-4 mr-1" /> Cancel
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="bg-mb-surface border-mb-border text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light text-2xl">Cancel appointment</DialogTitle>
            <DialogDescription className="text-mb-fg2 text-sm">Patient will be notified by inapp + mock email + mock SMS.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Reason</Label>
            <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="hospital-cancel-reason"
              className="bg-mb-surface2 border-mb-border text-white h-11 rounded-lg" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelTarget(null)} className="hover:bg-mb-surface2">Close</Button>
            <Button onClick={doCancel} disabled={busy} data-testid="hospital-confirm-cancel" className="bg-red-500 hover:bg-red-400 text-white">
              {busy ? "Cancelling…" : "Cancel appointment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
